import { useMemo, useState } from 'react';
import { Button, Card, Input, LoadingSkeleton, Tabs } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useUsers } from '../iam/useIamUsers';
import {
  useCommissionConfigs,
  useCreateCommissionConfig,
  useDeleteCommissionConfig,
  useCommissionEntries,
  useCommissionPayouts,
  useCreateCommissionPayout,
  usePayCommissionPayout,
} from './useCommissions';

type CommissionTab = 'painel' | 'regras' | 'pagamento';

const TABS: Array<{ key: CommissionTab; label: string }> = [
  { key: 'painel', label: 'Painel' },
  { key: 'regras', label: 'Regras de Comissão' },
  { key: 'pagamento', label: 'Pagamento' },
];

function sellerName(users: { id: string; name: string }[] | undefined, id: string): string {
  return users?.find((u) => u.id === id)?.name ?? id.slice(0, 8);
}

export function CommissionsPage(): JSX.Element {
  const [tab, setTab] = useState<CommissionTab>('painel');

  return (
    <div className="p-xl">
      <div className="mb-lg">
        <h1 className="m-0 font-display-lg text-display-lg text-on-surface">Comissões</h1>
        <p className="mt-xs font-body-md text-body-md text-on-surface-variant">
          Painel, regras e pagamento de comissões da equipe comercial.
        </p>
      </div>
      <div className="mb-lg">
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={(key) => setTab(key as CommissionTab)}
          aria-label="Abas de comissões"
        />
      </div>
      {tab === 'painel' ? <PainelTab /> : null}
      {tab === 'regras' ? <RegrasTab /> : null}
      {tab === 'pagamento' ? <PagamentoTab /> : null}
    </div>
  );
}

function PainelTab(): JSX.Element {
  const entriesQuery = useCommissionEntries();
  const usersQuery = useUsers();
  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);
  const users = usersQuery.data;

  const totals = useMemo(() => {
    const earned = entries.reduce((sum, e) => sum + e.commissionAmount, 0);
    const pending = entries
      .filter((e) => e.status === 'OPEN')
      .reduce((sum, e) => sum + e.commissionAmount, 0);
    return { earned, pending };
  }, [entries]);

  const monthly = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const e of entries) {
      const d = new Date(e.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      buckets.set(key, (buckets.get(key) ?? 0) + e.commissionAmount);
    }
    return [...buckets.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).slice(-6);
  }, [entries]);

  const topSellers = useMemo(() => {
    const bySeller = new Map<string, number>();
    for (const e of entries) {
      bySeller.set(e.salesPersonId, (bySeller.get(e.salesPersonId) ?? 0) + e.commissionAmount);
    }
    return [...bySeller.entries()].sort(([, a], [, b]) => b - a).slice(0, 5);
  }, [entries]);

  const maxMonth = Math.max(1, ...monthly.map(([, v]) => v));

  if (entriesQuery.isLoading) return <LoadingSkeleton count={3} height="96px" />;
  if (entriesQuery.error) {
    return (
      <p className="font-body-md text-body-md text-error">
        {(entriesQuery.error as Error).message}
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <Card>
        <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">
          Nenhuma comissão apurada
        </p>
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          Apurações nascem ao liquidar um título a receber com vendedor vinculado.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Total apurado
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(totals.earned)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Aguardando pagamento
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-warning-amber">
            {formatCurrency(totals.pending)}
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
          Ganhos por mês
        </h3>
        <div className="flex min-h-24 items-end gap-xs rounded bg-surface-container-low p-xs">
          {monthly.map(([month, value]) => (
            <div key={month} className="flex max-w-16 flex-1 flex-col items-center gap-xs">
              <div
                className="w-full rounded-t bg-primary"
                style={{ height: `${Math.max(6, (value / maxMonth) * 96)}px` }}
                title={`${month}: ${formatCurrency(value)}`}
              />
              <span className="font-label-sm text-label-sm text-on-surface-variant">
                {month.slice(5)}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-md xl:grid-cols-2">
        <Card padding="0">
          <h3 className="m-0 border-b border-outline-variant p-md font-headline-sm text-headline-sm text-on-surface">
            Top vendedores
          </h3>
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {topSellers.map(([sellerId, total], index) => (
              <li
                key={sellerId}
                className="flex items-center justify-between border-b border-outline-variant p-md last:border-b-0"
              >
                <span className="font-body-md text-body-md text-on-surface">
                  {index + 1}. {sellerName(users, sellerId)}
                </span>
                <span className="font-label-md text-label-md tabular-nums text-on-surface">
                  {formatCurrency(total)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card padding="0">
          <h3 className="m-0 border-b border-outline-variant p-md font-headline-sm text-headline-sm text-on-surface">
            Últimas apurações
          </h3>
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {entries.slice(0, 5).map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between border-b border-outline-variant p-md last:border-b-0"
              >
                <span className="font-body-sm text-body-sm text-on-surface-variant">
                  {sellerName(users, e.salesPersonId)} · {formatDate(e.createdAt)} · {e.status}
                </span>
                <span className="font-label-md text-label-md tabular-nums text-on-surface">
                  {formatCurrency(e.commissionAmount)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function RegrasTab(): JSX.Element {
  const configsQuery = useCommissionConfigs();
  const createConfig = useCreateCommissionConfig();
  const deleteConfig = useDeleteCommissionConfig();
  const usersQuery = useUsers();

  const [sellerId, setSellerId] = useState('');
  const [mode, setMode] = useState<'percentage' | 'value'>('percentage');
  const [amount, setAmount] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [formError, setFormError] = useState('');

  const configs = configsQuery.data ?? [];

  const submit = (): void => {
    if (!sellerId) {
      setFormError('Escolha o vendedor.');
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setFormError('Informe percentual ou valor maior que zero.');
      return;
    }
    setFormError('');
    createConfig.mutate(
      {
        salesPersonId: sellerId,
        ...(mode === 'percentage' ? { percentage: value } : { value }),
        ...(validFrom ? { validFrom } : {}),
        ...(validUntil ? { validUntil } : {}),
      },
      {
        onSuccess: () => {
          setSellerId('');
          setAmount('');
          setValidFrom('');
          setValidUntil('');
        },
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  return (
    <div className="flex flex-col gap-md">
      <Card>
        <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
          Nova regra
        </h3>
        <div className="mb-sm flex gap-sm">
          <button
            type="button"
            onClick={() => setMode('percentage')}
            aria-pressed={mode === 'percentage'}
            className={`rounded-md border px-sm py-xs font-label-md text-label-md ${
              mode === 'percentage'
                ? 'border-primary bg-primary-container font-semibold text-on-primary-container'
                : 'border-outline-variant text-on-surface-variant'
            }`}
          >
            Percentual %
          </button>
          <button
            type="button"
            onClick={() => setMode('value')}
            aria-pressed={mode === 'value'}
            className={`rounded-md border px-sm py-xs font-label-md text-label-md ${
              mode === 'value'
                ? 'border-primary bg-primary-container font-semibold text-on-primary-container'
                : 'border-outline-variant text-on-surface-variant'
            }`}
          >
            Valor fixo R$
          </button>
        </div>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <label className="flex flex-col gap-xs font-label-md text-label-md text-on-surface-variant">
            Vendedor *
            <select
              aria-label="Vendedor"
              className="rounded-md border border-outline-variant bg-surface-container-lowest px-sm py-xs font-body-md text-body-md text-on-surface"
              value={sellerId}
              onChange={(e) => setSellerId(e.target.value)}
            >
              <option value="">Selecione…</option>
              {(usersQuery.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <Input
            id="rule-amount"
            label={mode === 'percentage' ? 'Percentual *' : 'Valor (R$) *'}
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Input
            id="rule-from"
            label="Vigência inicial"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
          <Input
            id="rule-until"
            label="Vigência final"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>
        {formError ? (
          <p className="mb-sm font-body-sm text-body-sm text-error">{formError}</p>
        ) : null}
        <div className="mt-sm">
          <Button variant="primary" loading={createConfig.isPending} onClick={submit}>
            Salvar regra
          </Button>
        </div>
      </Card>

      <Card padding="0">
        <h3 className="m-0 border-b border-outline-variant p-md font-headline-sm text-headline-sm text-on-surface">
          Regras vigentes
        </h3>
        {configsQuery.isLoading ? (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={3} height="48px" />
          </div>
        ) : configs.length === 0 ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Sem regras cadastradas.
          </p>
        ) : (
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {configs.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md last:border-b-0"
              >
                <span className="font-body-md text-body-md text-on-surface">
                  {sellerName(usersQuery.data, c.salesPersonId)} ·{' '}
                  {c.percentage !== null ? `${c.percentage}%` : formatCurrency(c.value ?? 0)}
                  {c.validFrom || c.validUntil ? (
                    <span className="text-on-surface-variant">
                      {' '}
                      ({c.validFrom ? formatDate(c.validFrom) : '…'} →{' '}
                      {c.validUntil ? formatDate(c.validUntil) : '…'})
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="font-label-md text-label-md text-error hover:underline"
                  disabled={deleteConfig.isPending}
                  onClick={() => deleteConfig.mutate(c.id)}
                >
                  Excluir
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function PagamentoTab(): JSX.Element {
  const entriesQuery = useCommissionEntries();
  const payoutsQuery = useCommissionPayouts();
  const createPayout = useCreateCommissionPayout();
  const payPayout = usePayCommissionPayout();
  const [actionError, setActionError] = useState('');

  const openEntries = (entriesQuery.data ?? []).filter((e) => e.status === 'OPEN');
  const openTotal = openEntries.reduce((sum, e) => sum + e.commissionAmount, 0);
  const payouts = payoutsQuery.data ?? [];

  const newPayout = (): void => {
    setActionError('');
    createPayout.mutate(
      {},
      { onError: (err) => setActionError((err as Error).message) },
    );
  };

  return (
    <div className="flex flex-col gap-md">
      <Card>
        <div className="flex flex-col gap-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
              Aberto para pagamento
            </p>
            <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
              {formatCurrency(openTotal)}
            </p>
            <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
              {openEntries.length} apuração{openEntries.length === 1 ? '' : 'ões'} em aberto
            </p>
          </div>
          <Button
            variant="primary"
            loading={createPayout.isPending}
            disabled={openEntries.length === 0}
            onClick={newPayout}
          >
            Gerar lote
          </Button>
        </div>
        {actionError ? (
          <p className="mb-0 mt-sm font-body-sm text-body-sm text-error">{actionError}</p>
        ) : null}
      </Card>

      <Card padding="0">
        <h3 className="m-0 border-b border-outline-variant p-md font-headline-sm text-headline-sm text-on-surface">
          Lotes
        </h3>
        {payoutsQuery.isLoading ? (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={3} height="48px" />
          </div>
        ) : payouts.length === 0 ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Nenhum lote gerado.
          </p>
        ) : (
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {payouts.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md last:border-b-0"
              >
                <span className="font-body-md text-body-md text-on-surface">
                  {formatCurrency(p.totalAmount)} · {p.status}
                  {p.paidAt ? ` · pago em ${formatDate(p.paidAt)}` : ''}
                </span>
                {p.status !== 'PAID' ? (
                  <button
                    type="button"
                    className="font-label-md text-label-md text-primary hover:underline"
                    disabled={payPayout.isPending}
                    onClick={() => payPayout.mutate(p.id)}
                  >
                    Marcar como pago
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
